import React, { useEffect, useRef, useState } from "react";
import { ApiPaths } from "../../Config/ApiPath";
import PaginationComponent from "../../Components/PaginationControls/PaginationControls";
import { BasicInfo, toastFailed, toastSuccess } from "../../Config/BasicInfo";
import useAxiosHelper from "../../Common/AxiosHelper";
import moment from "moment/moment";
import PrincipalWithdrawalHistory from "./PrincipalWithdrawalHistory";
import "./PackageApp.css";
import {
  Package as PackageIcon,
  Calendar,
  SlidersHorizontal,
  X,
  RotateCcw,
  Search,
  Inbox,
  TrendingUp,
  Briefcase,
  History,
  Hash,
} from "lucide-react";

const statusOf = (s) =>
  s == "1"
    ? { key: "on", label: "Active" }
    : s == "2"
    ? { key: "expired", label: "Expired" }
    : s == "3"
    ? { key: "withdrawn", label: "Withdrawn" }
    : { key: "pending", label: "Pending" };

const money = (n) =>
  `$${(parseFloat(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Order = ({ updateOrderList }) => {
  const [filterVisiblity, setFilterVisiblity] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentTransaction, setPaymentTransaction] = useState();
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [confirmPrincipal, setConfirmPrincipal] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [limitFilter, setLimitFilter] = useState(10);
  const [previewData, setPreviewData] = useState();
  const [showWithdrawal, setShowWithdrawal] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [totalSum, setTotalSum] = useState(null);
  const [totalRecords, setTotalRecords] = useState(null);
  const { AxiosGet, AxiosPost } = useAxiosHelper();
  const [search, setSearch] = useState({
    startDate: "",
    endDate: "",
  });

  // Refs keep latest values so FetchData can be called from handlers without useEffect
  const paramsRef = useRef({ currentPage: 1, limitFilter: 10, search: { startDate: "", endDate: "" } });
  paramsRef.current = { currentPage, limitFilter, search };

  const handlePagination = (page) => {
    setCurrentPage(page);
    paramsRef.current = { ...paramsRef.current, currentPage: page };
    FetchData();
  };

  // Single effect: only for initial load and when parent signals refresh (updateOrderList)
  useEffect(() => {
    FetchData();
  }, [updateOrderList]);

  // sheets: Escape closes, page behind stays put
  const sheetOpen = filterVisiblity || confirmPrincipal;
  useEffect(() => {
    if (!sheetOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setFilterVisiblity(false);
        setConfirmPrincipal(false);
      }
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [sheetOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearch((prev) => ({ ...prev, [name]: value }));
  };

  const AddFilter = () => {
    const queryParams = {
      page: currentPage,
      limit: limitFilter,
      ...Object.fromEntries(
        Object.entries(search).filter(
          ([_, value]) => value !== "" && value !== 0
        )
      ),
    };
    FetchData(queryParams);
    setFilterVisiblity(false);
  };

  const FetchData = async (customParams = null) => {
    try {
      const { currentPage: p, limitFilter: l, search: s } = paramsRef.current;
      const filteredSearch = Object.fromEntries(
        Object.entries(customParams ? customParams : s).filter(([_, value]) => value !== "" && value !== undefined)
      );
      const queryParams = customParams
        ? { page: customParams.page ?? p, limit: customParams.limit ?? l, ...filteredSearch }
        : { page: p, limit: l, ...Object.fromEntries(Object.entries(s).filter(([_, v]) => v !== "")) };

      setLoading(true);

      const response = await AxiosGet(
        `${ApiPaths.getOrders}?${new URLSearchParams(queryParams).toString()}`
      );
      BasicInfo.isDebug && console.log("order history", response);
      setPaymentTransaction(response?.data || []);
      setTotalPages(response?.totalPages || 1);
      setTotalSum(response?.Sum ?? null);
      setTotalRecords(response?.totalRecords ?? null);
    } catch (error) {
      BasicInfo.isDebug &&
        console.error("Error fetching payment transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  function resetData() {
    setSearch({ startDate: "", endDate: "" });
    setCurrentPage(1);
    setLimitFilter(10);
    setFilterVisiblity(false);
    paramsRef.current = { ...paramsRef.current, currentPage: 1, limitFilter: 10, search: { startDate: "", endDate: "" } };
    FetchData({ page: 1, limit: 10 });
  }

  const handleConfirmModal = (orderId) => {
    setOrderId(orderId);
    principalPreview(orderId);
    setConfirmPrincipal(true);
  };

  async function principalWithdraw() {
    try {
      const res = await AxiosPost(ApiPaths.principalWithdraw, { orderId });
      if (res) {
        toastSuccess(res?.message);
        setConfirmPrincipal(false);
        setRefresh((prev) => !prev);
        FetchData();
      }
    } catch (error) {
      toastFailed(error?.message || "Withdrawal failed");
    }
  }

  async function principalPreview(orderId) {
    try {
      const res = await AxiosPost(ApiPaths.principalPreview, { orderId });
      if (res) {
        toastSuccess(res?.message);
        setPreviewData(res);
      }
    } catch (error) {
      toastFailed(error?.message);
    }
  }

  const handleWithdrawalHistory = () => {
    setShowWithdrawal(true);
  };

  const list = Array.isArray(paymentTransaction) ? paymentTransaction : [];
  const pageEarned = list.reduce((s, x) => s + (parseFloat(x?.totalEarned) || 0), 0);
  const activeOnPage = list.filter((x) => x?.status == "1").length;
  const filtersOn = Boolean(search.startDate || search.endDate);
  const firstLoad = paymentTransaction === undefined;

  if (showWithdrawal) {
    return (
      <section className="pkHistory">
        <PrincipalWithdrawalHistory refresh={refresh} onBack={() => setShowWithdrawal(false)} />
      </section>
    );
  }

  return (
    <section className="pkHistory">
      {/* ---------- Summary ---------- */}
      <div className="pkHistSummary">
        <div>
          <span className="pkHsIcon">
            <Briefcase size={17} />
          </span>
          <small>Total invested</small>
          <b>{totalSum != null ? money(totalSum) : money(list.reduce((s, x) => s + (parseFloat(x?.amount) || 0), 0))}</b>
        </div>
        <div>
          <span className="pkHsIcon mint">
            <TrendingUp size={17} />
          </span>
          <small>Earned{totalPages > 1 ? " (this page)" : ""}</small>
          <b>{money(pageEarned)}</b>
        </div>
        <div>
          <span className="pkHsIcon blue">
            <PackageIcon size={17} />
          </span>
          <small>Packages</small>
          <b>
            {totalRecords ?? list.length}
            {activeOnPage > 0 && <em>{activeOnPage} active</em>}
          </b>
        </div>
      </div>

      {/* ---------- Toolbar ---------- */}
      <div className="pkHistBar">
        <h2>Package history</h2>
        <button type="button" className="pkGhostBtn" onClick={handleWithdrawalHistory}>
          <History size={15} /> Withdrawals
        </button>
        <button
          type="button"
          className={`pkFilterBtn ${filtersOn ? "isOn" : ""}`}
          onClick={() => setFilterVisiblity(true)}
          aria-label="Filter by date"
        >
          <SlidersHorizontal size={18} />
          {filtersOn && <i />}
        </button>
      </div>

      {filtersOn && (
        <div className="pkActiveFilter">
          <Calendar size={13} />
          {search.startDate ? moment(search.startDate).format("DD MMM YYYY") : "Start"} –{" "}
          {search.endDate ? moment(search.endDate).format("DD MMM YYYY") : "Today"}
          <button type="button" onClick={resetData} aria-label="Clear date filter">
            <X size={13} />
          </button>
        </div>
      )}

      {/* ---------- List ---------- */}
      {firstLoad || (loading && !list.length) ? (
        <div className="pkList">
          {[0, 1, 2].map((i) => (
            <div key={i} className="pkOrder pkOrderSkel">
              <span className="a" />
              <span className="b" />
              <span className="c" />
            </div>
          ))}
        </div>
      ) : list.length === 0 ? (
        <div className="pkEmpty">
          <span>
            <Inbox size={26} />
          </span>
          <b>No packages yet</b>
          <small>
            {filtersOn ? "Nothing in this date range." : "Your purchased packages and their earnings will appear here."}
          </small>
          {filtersOn && (
            <button type="button" onClick={resetData}>
              <RotateCcw size={14} /> Clear filter
            </button>
          )}
        </div>
      ) : (
        <div className={`pkList ${loading ? "isBusy" : ""}`}>
          {list.map((x, i) => {
            const st = statusOf(x?.status);
            const earned = parseFloat(x?.totalEarned) || 0;
            const amt = parseFloat(x?.amount) || 0;
            const earnedPct = amt > 0 ? Math.min(100, (earned / amt) * 100) : 0;
            return (
              <article key={x?._id ?? x?.order_Id ?? i} className={`pkOrder st-${st.key}`}>
                <div className="pkOrderTop">
                  <span className="pkOrderIcon">
                    <PackageIcon size={20} />
                  </span>
                  <span className="pkOrderMain">
                    <b>{money(amt)}</b>
                    <small>
                      <Hash size={11} />
                      {x?.order_Id ?? (currentPage - 1) * limitFilter + (i + 1)}
                      {x?.username ? ` · ${x.username}` : ""}
                    </small>
                  </span>
                  <span className={`pkStatus ${st.key}`}>{st.label}</span>
                </div>

                <div className="pkOrderEarn">
                  <div className="pkOrderEarnRow">
                    <span>ROI earned</span>
                    <b>{money(earned)}</b>
                  </div>
                  <span className="pkEarnBar">
                    <span style={{ width: `${earnedPct}%` }} />
                  </span>
                </div>

                <div className="pkOrderFoot">
                  <span>
                    <Calendar size={12} /> {moment(x?.createdAt).format("DD MMM YYYY, hh:mm A")}
                  </span>
                  <span className="pkOrderNo">#{(currentPage - 1) * limitFilter + (i + 1)}</span>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {list.length > 0 && (
        <div className="pkPager">
          <PaginationComponent currentPage={currentPage} totalPages={totalPages} onPageChange={handlePagination} />
        </div>
      )}

      {/* ---------- Date filter sheet ---------- */}
      {filterVisiblity && (
        <div className="pkOverlay" onClick={() => setFilterVisiblity(false)}>
          <div className="pkSheet" role="dialog" aria-modal="true" aria-labelledby="pkFilterTitle" onClick={(e) => e.stopPropagation()}>
            <span className="pkHandle" aria-hidden="true" />
            <button type="button" className="pkClose" onClick={() => setFilterVisiblity(false)} aria-label="Close">
              <X size={18} />
            </button>
            <h3 id="pkFilterTitle">Filter by date</h3>
            <p className="pkSheetSub">Show packages bought in this range</p>
            <div className="pkFieldRow">
              <label className="pkField">
                <span>From</span>
                <input type="date" name="startDate" value={search.startDate} onChange={handleChange} />
              </label>
              <label className="pkField">
                <span>To</span>
                <input type="date" name="endDate" value={search.endDate} onChange={handleChange} />
              </label>
            </div>
            <div className="pkSheetActions">
              <button type="button" className="pkBtn ghost" onClick={resetData}>
                <RotateCcw size={16} /> Reset
              </button>
              <button type="button" className="pkBtn primary" onClick={AddFilter}>
                <Search size={16} /> Show results
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Principal withdrawal approval ---------- */}
      {confirmPrincipal && (
        <div className="pkOverlay" onClick={() => setConfirmPrincipal(false)}>
          <div className="pkSheet" role="dialog" aria-modal="true" aria-labelledby="pkPrincipalTitle" onClick={(e) => e.stopPropagation()}>
            <span className="pkHandle" aria-hidden="true" />
            <button type="button" className="pkClose" onClick={() => setConfirmPrincipal(false)} aria-label="Close">
              <X size={18} />
            </button>
            <h3 id="pkPrincipalTitle">Withdraw principal?</h3>
            <p className="pkSheetSub">
              The immediately withdrawable amount returns to your wallet; 20% of this package amount is held.
            </p>
            {previewData?.eligible ? (
              <dl className="pkSummary">
                <div>
                  <dt>Immediate withdrawable</dt>
                  <dd>${parseFloat(previewData?.immediately_withdrawable).toFixed(4)}</dd>
                </div>
                <div>
                  <dt>Hold amount</dt>
                  <dd>${parseFloat(previewData?.hold_amount).toFixed(4)}</dd>
                </div>
                <div>
                  <dt>Hold until</dt>
                  <dd>{moment(previewData?.hold_until).format("lll")}</dd>
                </div>
              </dl>
            ) : (
              <div className="pkWarn">
                <span>
                  {previewData?.reason}
                  {previewData?.totalIncome !== undefined && ` · Total earned ${money(previewData?.totalIncome)}`}
                </span>
              </div>
            )}
            {previewData?.eligible && (
              <>
                <p className="pkSheetNote">Withdrawing your principal may stop future rewards from this package.</p>
                <div className="pkSheetActions">
                  <button type="button" className="pkBtn ghost" onClick={() => setConfirmPrincipal(false)}>
                    No, keep it
                  </button>
                  <button type="button" className="pkBtn primary" onClick={principalWithdraw} disabled={!previewData?.eligible}>
                    Yes, withdraw
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default Order;
